import { useForm, FormProvider } from 'react-hook-form'
import isEqual from 'lodash/isEqual'
import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useRef,
} from 'react'
import { ChevronDown, MoreHorizontal, PencilLine, Reply, X } from 'lucide-react'
import { cn } from 'tailwind-v4/lib/utils'
import { Button } from 'tailwind-v4/components/button'
import { Label } from 'tailwind-v4/components/label'
import { Textarea } from 'tailwind-v4/components/textarea'
import { Spinner } from 'tailwind-v4/components/spinner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'tailwind-v4/components/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'tailwind-v4/components/popover'
import {
  RichTextEditor,
  type RichTextEditorHandle,
} from 'tailwind-v4/components/rich-text-editor'
import { useParams } from 'react-router'
import { useIsMobile } from '@/hooks/use-mobile'
import { useBoolean } from '@/hooks/use-color-mode-standalone'
import { useVenues } from '@/hooks/useVenue'
import useUser from '@/hooks/useUser'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import inboxApi from '@/state/inbox/inbox.slice'
import { initThread } from '@/state/entities/inbox/inbox.types'
import { MergeTagInsertButton } from '../merge-tag-insert-button'
import { AssetInsertButton } from '../media/asset-insert-button'
import { AssetBadgeRow } from '../media/asset-badge-row'
import { EmailRecipients } from './email-recipients'
import { TemplateInsertButton } from './template-insert-button'
import { SuggestReplyButton } from '@/components/EnquiriesWorkspace/suggest/SuggestReplyButton'

interface ThreadMessageProps extends React.HTMLAttributes<HTMLDivElement> {
  onChangeSendType: (type: 'messages' | 'notes') => void
  sendType: 'messages' | 'notes'
  onComplete: () => void
  threadId: string
  content?: string
  /** When the thread belongs to an enquiry, the Coordinator can draft the next reply from the whole file (zion A3). */
  suggestFor?: { enquiryId: string }
}

interface ContactFormValues {
  contacts: {
    email: string
    name: string
    id?: string | null | undefined
    type?: string
  }[]
}

interface MessageFormValues {
  description: string
}

function hasNotch() {
  if (CSS.supports('padding-bottom: env(safe-area-inset-bottom)')) {
    const div = document.createElement('div')
    div.style.paddingBottom = 'env(safe-area-inset-bottom)'
    document.body.appendChild(div)
    const calculatedPadding = parseInt(
      window.getComputedStyle(div).paddingBottom,
      10
    )
    document.body.removeChild(div)
    if (calculatedPadding > 0) {
      return true
    }
  }
  return false
}

/**
 *
 * @todo - put these in another DIR
 */
const ThreadSendMessageBox: FunctionComponent<ThreadMessageProps> = ({
  onChangeSendType,
  sendType,
  onComplete,
  suggestFor,
  threadId,
  content,
}) => {
  const { org_id: orgId = '' } = useParams<{ org_id: string }>()

  const { data: thread = initThread, isLoading } =
    inboxApi.useGetInboxThreadQuery(
      {
        orgId,
        threadId,
      }
      // {
      //   skip: !threadId,
      // }
    )

  const [createContact] = inboxApi.useCreateEmailContactsOnThreadMutation()

  const [removeContact] = inboxApi.useRemoveEmailContactsOnThreadMutation()

  const [sendEvents, { isLoading: isSending }] =
    inboxApi.useSendInboxThreadEventsMutation()

  // Contact form with React Hook Form
  const contactMethods = useForm<ContactFormValues>({
    defaultValues: {
      contacts: thread.contacts ?? [],
    },
    mode: 'onChange',
  })

  const {
    handleSubmit: handleContactSubmit,
    formState: { errors: contactErrors },
    watch: watchContacts,
    reset: resetContactForm,
    setValue: setContactValue,
  } = contactMethods

  // Bind recipients directly to the thread's contacts (the cache the
  // create/remove mutations update) so chip ids always match the backend.
  const contacts = thread.contacts ?? []

  const [editEmail, setEditEmail] = useBoolean(contacts.length === 0)

  // Main message form with React Hook Form
  const messageMethods = useForm<MessageFormValues>({
    defaultValues: {
      description: content || '',
    },
    mode: 'onChange',
  })

  const {
    handleSubmit: handleMessageSubmit,
    formState: { errors: messageErrors, isValid },
    reset: resetMessageForm,
    setValue: setMessageValue,
    watch: watchMessage,
  } = messageMethods

  const description = watchMessage('description')
  const editorRef = useRef<RichTextEditorHandle>(null)
  const isMobile = useIsMobile()

  // Merge-tag resolution: in the composer we know the recipient/venue/org/agent,
  // so inserting a tag drops the real value (with friendly fallbacks matching
  // the backend) rather than a `{{token}}`. Templates keep the brace token.
  const [, findVenueAlias] = useVenues()
  const org = useSelectedOrganisation()
  const user = useUser()
  const resolveMergeTag = (id: string): string => {
    const recipient: any = thread.contacts?.[0]
    const fullName = (recipient?.name ?? '').trim()
    switch (id) {
      case 'first_name':
        return fullName.split(/\s+/)[0] || 'there'
      case 'contact_name':
        return fullName || 'there'
      case 'contact_email':
        return recipient?.email ?? ''
      case 'venue_name':
        return (
          (thread.serial ? findVenueAlias(thread.serial) : '') ||
          org?.name ||
          'us'
        )
      case 'org_name':
        return org?.name || 'us'
      case 'agent_name':
        return [user?.first, user?.last].filter(Boolean).join(' ') || 'the team'
      default:
        return ''
    }
  }

  /**
   * @todo - refactor this
   */
  useEffect(() => {
    if (contacts.length === 0) {
      setEditEmail.on()
    } else {
      setEditEmail.off()
    }
  }, [contacts.length])

  // Handle contacts form submission
  const onContactSubmit = async (values: ContactFormValues) => {
    console.log({ contacts: values.contacts })
    resetContactForm()
  }

  // Handle message form submission
  const onMessageSubmit = async (values: MessageFormValues) => {
    await sendEvents({
      type: sendType,
      description: values.description,
      orgId,
      threadId: thread.id,
      format: thread.method === 'email' ? 'html' : 'text',
    })

    onComplete()
    resetMessageForm()
  }

  useEffect(() => {
    // Update contacts when thread changes
    if (thread.contacts?.length) {
      setContactValue('contacts', thread.contacts)
    }
  }, [thread.contacts])

  useEffect(() => {
    // Update initial content when it changes
    if (content) {
      setMessageValue('description', content)
    }
  }, [])

  useEffect(() => {
    const initialContacts = thread.contacts || []
    if (!isEqual(initialContacts, contacts)) {
      console.log({ newContacts: 'hello' }, contacts)
    }
  }, [contacts, thread.contacts])

  {
    /**
    be careful of altering this as its used in a few parts of the product.
    if editing please remove chakra where used.
    */
  }

  // Merge tag / asset / template. Three pills fit the editor toolbar on
  // desktop; on a phone they crowd it off the row, so they collapse into a `⋯`
  // popover as full-width rows. They live INSIDE that popover's content on
  // purpose: each owns its own popover/dialog, and base-ui only treats those as
  // nested (so opening one doesn't dismiss the `⋯`) when they're in its subtree.
  const insertActionRow = 'w-full justify-start rounded-md px-2 py-1.5 text-sm'
  let insertActions: ReactNode
  if (thread.id) {
    const actions = (
      <>
        <MergeTagInsertButton
          className={isMobile ? insertActionRow : undefined}
          resolve={resolveMergeTag}
          onInsert={(token) => editorRef.current?.insertContent(token)}
        />
        <AssetInsertButton
          className={isMobile ? insertActionRow : undefined}
          orgId={orgId}
          threadId={thread.id}
          serial={thread.serial}
          onInsert={(html) => editorRef.current?.insertContent(html)}
        />
        <TemplateInsertButton
          className={isMobile ? insertActionRow : undefined}
          orgId={orgId}
          threadId={thread.id}
          serial={thread.serial}
          onInsert={(html) => editorRef.current?.insertContent(html)}
        />
        {suggestFor ? (
          <SuggestReplyButton
            className={isMobile ? insertActionRow : undefined}
            orgId={orgId}
            enquiryId={suggestFor.enquiryId}
            onInsert={(html) => editorRef.current?.insertContent(html)}
          />
        ) : null}
      </>
    )

    insertActions = isMobile ? (
      <Popover>
        <PopoverTrigger
          render={
            // `type="button"`: the v4 Button sets no default type and this sits
            // inside the composer's <form>, so without it tapping ⋯ would send
            // the message. `icon-sm` + the Button's default pill shape = a
            // circle.
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Insert merge tag, asset or template"
            />
          }
        >
          <MoreHorizontal />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="new-ui-theme flex w-52 flex-col gap-1 p-1"
        >
          {actions}
        </PopoverContent>
      </Popover>
    ) : (
      actions
    )
  }

  return (
    <div
      className={cn(
        // `new-ui-theme` is applied here directly so the v4 color tokens
        // (--foreground, --muted-foreground, --border, etc.) resolve even when
        // this composer is mounted outside the org layout — e.g. the venue
        // DayInboxPanel / EnquiryInbox, where the text/icons were rendering
        // white-on-white because the tokens were undefined.
        'new-ui-theme relative z-20 mx-3 mb-3 flex flex-col gap-1 rounded-xl p-3 text-foreground',
        {
          'bg-yellow-400/80 dark:bg-yellow-600/80 backdrop-blur-lg':
            sendType === 'notes',
          // The raised composer surface. Dark was already on `--popover`;
          // light was stuck on `neutral-100`, a cold grey that read as a pale
          // slab once the canvas went warm stone. One token, both themes.
          'bg-popover/75 backdrop-blur-lg': sendType !== 'notes',
          'max-lg:mb-[calc(0.5rem+env(safe-area-inset-bottom))]': hasNotch(),
          'max-lg:mb-': !hasNotch(),
        }
      )}
    >
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <button
                type="button"
                className={cn(
                  'inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-sm capitalize transition-colors',
                  // Tokens, so the pill follows the canvas instead of pinning
                  // a white/black tint that fights the stone surface.
                  'border-border bg-background/60 text-muted-foreground hover:bg-foreground/5'
                )}
              >
                {sendType === 'notes' ? (
                  <PencilLine className="size-3.5 opacity-60" />
                ) : (
                  <Reply className="size-3.5 opacity-60" />
                )}
                {sendType === 'notes' ? 'note' : thread.method}
                <ChevronDown className="size-3.5 opacity-60" />
              </button>
            }
          />
          <DropdownMenuContent align="start" className="w-40">
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  onChangeSendType('notes')
                }}
              >
                Note
              </DropdownMenuItem>
              <DropdownMenuItem
                className="capitalize"
                onClick={() => {
                  onChangeSendType('messages')
                }}
              >
                {thread.method}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {!editEmail && sendType === 'messages' && contacts.length > 0 && (
          <button
            type="button"
            onClick={setEditEmail.toggle}
            className="min-w-0 truncate text-sm text-muted-foreground hover:text-foreground hover:underline"
          >
            {contacts.map((contact: any) => contact.email).join(', ')}
          </button>
        )}
        {editEmail && sendType === 'messages' ? (
          <div className="flex w-full items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Collapse recipients"
              onClick={setEditEmail.toggle}
            >
              <X className="size-4" />
            </Button>
            <Label className="shrink-0 text-muted-foreground">To:</Label>
            <EmailRecipients
              contacts={contacts}
              onAdd={(email) => {
                if (!thread?.id) return
                createContact({
                  orgId,
                  threadId: thread.id,
                  data: { email, name: '', method: 'email' },
                })
              }}
              onRemove={(cont) => {
                if (!cont?.id || !thread?.id) return
                removeContact({
                  orgId,
                  threadId: thread.id,
                  contactId: cont.id,
                })
              }}
            />
          </div>
        ) : null}
      </div>

      <FormProvider {...messageMethods}>
        <form
          className="w-full"
          onSubmit={handleMessageSubmit(onMessageSubmit)}
        >
          {/* The Send button floats in the bottom-right of the editor; the
              content gets bottom padding so the last line clears it. */}
          <div className="relative w-full">
            {thread.method === 'email' ? (
              <RichTextEditor
                ref={editorRef}
                className="border-0 [&_.ProseMirror]:max-h-60 [&_.ProseMirror]:overflow-y-auto [&_.ProseMirror]:pb-10"
                value={description}
                onChange={(html) => {
                  setMessageValue('description', html, { shouldValidate: true })
                }}
                placeholder={
                  sendType === 'notes'
                    ? 'Write an internal note'
                    : 'Write a message'
                }
                toolbar={
                  isMobile ? { headings: false, quote: false } : undefined
                }
                belowToolbar={
                  <AssetBadgeRow
                    html={description}
                    onRemove={(url) => editorRef.current?.removeLinkByHref(url)}
                    onRename={(url, text) =>
                      editorRef.current?.setLinkTextByHref(url, text)
                    }
                  />
                }
                toolbarActions={insertActions}
              />
            ) : null}
            {thread.method === 'sms' ? (
              <Textarea
                disabled={!thread}
                value={description}
                onChange={(e) => {
                  setMessageValue('description', e.target.value, {
                    shouldValidate: true,
                  })
                }}
                placeholder={
                  sendType === 'notes'
                    ? 'Write an internal note'
                    : 'Write a message'
                }
                className="max-h-60 pb-12"
              />
            ) : null}
            <Button
              variant={sendType === 'notes' ? 'outline' : 'default'}
              size="sm"
              disabled={!thread || !isValid || isSending}
              leftIcon={isSending ? <Spinner /> : undefined}
              type="submit"
              className={cn(
                'absolute bottom-2 right-2 z-10',
                // Inverted via the ink/canvas tokens rather than a cold
                // `neutral-900`, so it warms with the palette and needs no
                // `dark:` twin.
                sendType !== 'notes' &&
                  'bg-foreground text-background hover:bg-foreground/90'
              )}
            >
              {sendType === 'notes' ? 'Save' : 'Send'}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default ThreadSendMessageBox
